import axios from 'axios';
import { resolveAuthHeaderValue } from '@/utils/authToken';
import { apiUrl } from '@/utils/appConfig';

const wordpressConceptApiClient = axios.create({
  baseURL: apiUrl(),
});

wordpressConceptApiClient.interceptors.request.use(config => {
  const authHeaderValue = resolveAuthHeaderValue();
  if (!authHeaderValue) return config;
  const nextConfig = { ...config };
  if (!nextConfig.headers) nextConfig.headers = {};
  nextConfig.headers.Authorization = authHeaderValue;
  return nextConfig;
});

const encode = value => encodeURIComponent(String(value || '').trim());
const trimTrailingSlash = value => String(value || '').replace(/\/+$/, '');

export async function getSiteTopicDictionaryValues() {
  const { data } = await wordpressConceptApiClient.get('/api/reference/dictionaries/site_topic/values');
  return Array.isArray(data) ? data : [];
}

export async function createSiteProject(payload) {
  const { data } = await wordpressConceptApiClient.post('/api/site-projects', payload);
  return data;
}

export async function getSiteProjects() {
  const { data } = await wordpressConceptApiClient.get('/api/site-projects');
  return Array.isArray(data) ? data : [];
}

export async function executeAiProfile(payload) {
  const { data } = await wordpressConceptApiClient.post('/api/ai/execute', payload);
  return data;
}

export async function saveSiteProjectConcepts(projectId, payload) {
  const { data } = await wordpressConceptApiClient.post(`/api/site-projects/${encode(projectId)}/concepts`, payload);
  return data;
}

export async function deleteSiteProjectConcept(projectId, conceptId) {
  await wordpressConceptApiClient.delete(`/api/site-projects/${encode(projectId)}/concepts/${encode(conceptId)}`);
}

export async function updateSiteProjectConcept(projectId, conceptId, payload) {
  const { data } = await wordpressConceptApiClient.put(`/api/site-projects/${encode(projectId)}/concepts/${encode(conceptId)}`, payload);
  return data;
}

export async function deleteSiteProject(projectId) {
  await wordpressConceptApiClient.delete(`/api/site-projects/${encode(projectId)}`);
}

export async function selectSiteProjectConcept(projectId, payload) {
  const { data } = await wordpressConceptApiClient.post(`/api/site-projects/${encode(projectId)}/select-concept`, payload);
  return data;
}

export async function getSiteProject(projectId) {
  const { data } = await wordpressConceptApiClient.get(`/api/site-projects/${encode(projectId)}`);
  return data;
}

export async function createSiteProjectIdentity(projectId, payload) {
  const { data } = await wordpressConceptApiClient.post(`/api/site-projects/${encode(projectId)}/identity`, payload);
  return data;
}

export async function updateSiteProjectIdentity(projectId, payload) {
  const { data } = await wordpressConceptApiClient.put(`/api/site-projects/${encode(projectId)}/identity`, payload);
  return data;
}

export async function getSiteProjectIdentity(projectId) {
  const { data } = await wordpressConceptApiClient.get(`/api/site-projects/${encode(projectId)}/identity`);
  return data;
}

export async function deleteSiteProjectIdentity(projectId) {
  await wordpressConceptApiClient.delete(`/api/site-projects/${encode(projectId)}/identity`);
}

export async function generateSiteProjectDesign(promptVariables) {
  return executeAiProfile({ profileKey: 'design_step', promptVariables });
}

export async function getSiteProjectDesign(projectId) {
  const { data } = await wordpressConceptApiClient.get(`/api/site-projects/${encode(projectId)}/design`);
  return data;
}

export async function updateSiteProjectDesign(projectId, payload) {
  const { data } = await wordpressConceptApiClient.put(`/api/site-projects/${encode(projectId)}/design`, payload);
  return data;
}

export async function deleteSiteProjectDesign(projectId) {
  await wordpressConceptApiClient.delete(`/api/site-projects/${encode(projectId)}/design`);
}

export async function addSiteProjectIdentityCategory(projectId, payload) {
  const { data } = await wordpressConceptApiClient.post(`/api/site-projects/${encode(projectId)}/identity/categories`, payload);
  return data;
}

export async function deleteSiteProjectIdentityCategory(projectId, categoryId) {
  await wordpressConceptApiClient.delete(`/api/site-projects/${encode(projectId)}/identity/categories/${encode(categoryId)}`);
}

export async function saveSiteProjectDomains(projectId, payload) {
  const { data } = await wordpressConceptApiClient.post(`/api/site-projects/${encode(projectId)}/domains`, payload);
  return data;
}

export async function addSiteProjectDomain(projectId, payload) {
  const { data } = await wordpressConceptApiClient.post(`/api/site-projects/${encode(projectId)}/domains/add`, payload);
  return data;
}

export async function selectSiteProjectDomain(projectId, payload) {
  const { data } = await wordpressConceptApiClient.post(`/api/site-projects/${encode(projectId)}/select-domain`, payload);
  return data;
}

export async function updateSiteProjectDomain(projectId, domainId, payload) {
  const { data } = await wordpressConceptApiClient.put(`/api/site-projects/${encode(projectId)}/domains/${encode(domainId)}`, payload);
  return data;
}

export async function deleteSiteProjectDomain(projectId, domainId) {
  await wordpressConceptApiClient.delete(`/api/site-projects/${encode(projectId)}/domains/${encode(domainId)}`);
}

export async function generateSiteProjectLogo(projectId, payload = {}) {
  const body = payload && typeof payload === 'object' ? payload : {};
  const { data } = await wordpressConceptApiClient.post(`/api/site-projects/${encode(projectId)}/logo/generate`, body);
  return data;
}

export async function getSiteProjectLogo(projectId) {
  const { data } = await wordpressConceptApiClient.get(`/api/site-projects/${encode(projectId)}/logo`);
  return data;
}

export async function cropSiteProjectLogo(projectId, payload) {
  const { data } = await wordpressConceptApiClient.post(`/api/site-projects/${encode(projectId)}/logo/crop`, payload);
  return data;
}

export async function composeSiteProjectLogo(projectId, payload) {
  const { data } = await wordpressConceptApiClient.post(`/api/site-projects/${encode(projectId)}/logo/compose`, payload);
  return data;
}

export async function selectSiteProjectLogoGeneration(projectId, payload) {
  const { data } = await wordpressConceptApiClient.post(`/api/site-projects/${encode(projectId)}/logo/generations/select`, payload);
  return data;
}

export async function selectSiteProjectLogoVariant(projectId, payload) {
  const { data } = await wordpressConceptApiClient.post(`/api/site-projects/${encode(projectId)}/logo/select`, payload);
  return data;
}

export async function selectSiteProjectLogoCandidate(projectId, payload) {
  const { data } = await wordpressConceptApiClient.post(`/api/site-projects/${encode(projectId)}/logo/candidates/select`, payload);
  return data;
}

export async function uploadSiteProjectLogo(projectId, file) {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await wordpressConceptApiClient.post(`/api/site-projects/${encode(projectId)}/logo/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function deleteSiteProjectLogo(projectId) {
  await wordpressConceptApiClient.delete(`/api/site-projects/${encode(projectId)}/logo`);
}

export async function deleteSiteProjectLogoCrop(projectId) {
  const { data } = await wordpressConceptApiClient.delete(`/api/site-projects/${encode(projectId)}/logo/crop`);
  return data;
}

export async function generateSiteProjectFavicon(projectId, payload = {}) {
  const body = payload && typeof payload === 'object' ? payload : {};
  const { data } = await wordpressConceptApiClient.post(`/api/site-projects/${encode(projectId)}/favicon/generate`, body);
  return data;
}

export async function getSiteProjectFavicon(projectId) {
  const { data } = await wordpressConceptApiClient.get(`/api/site-projects/${encode(projectId)}/favicon`);
  return data;
}

export async function uploadSiteProjectFavicon(projectId, file) {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await wordpressConceptApiClient.post(`/api/site-projects/${encode(projectId)}/favicon/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function createSiteProjectLocalization(projectId, payload) {
  const { data } = await wordpressConceptApiClient.post(`/api/site-projects/${encode(projectId)}/localizations`, payload);
  return data;
}

export async function getSiteProjectLocalization(projectId, languageCode) {
  const { data } = await wordpressConceptApiClient.get(`/api/site-projects/${encode(projectId)}/localizations/${encode(languageCode)}`);
  return data;
}

export async function deleteSiteProjectLocalization(projectId, localizationId) {
  await wordpressConceptApiClient.delete(`/api/site-projects/${encode(projectId)}/localizations/${encode(localizationId)}`);
}

export async function selectSiteProjectLocalization(projectId, payload) {
  const { data } = await wordpressConceptApiClient.post(`/api/site-projects/${encode(projectId)}/select-local`, payload);
  return data;
}

export async function clearSiteProjectLocalizationSelection(projectId) {
  const { data } = await wordpressConceptApiClient.delete(`/api/site-projects/${encode(projectId)}/select-local`);
  return data;
}

export async function getSiteProjectResolvedContent(projectId, locale = '') {
  const normalizedLocale = String(locale || '').trim();
  const path = normalizedLocale
    ? `/api/site-projects/${encode(projectId)}/resolved-content/${encode(normalizedLocale)}`
    : `/api/site-projects/${encode(projectId)}/resolved-content`;
  const { data } = await wordpressConceptApiClient.get(path);
  return data;
}

export async function startSiteProjectDomainAvailabilityCheck(projectId, payload) {
  const { data } = await wordpressConceptApiClient.post(`/api/site-projects/${encode(projectId)}/domains/availability-checks`, payload);
  return data;
}

export function buildSiteProjectDomainAvailabilityEventsUrl(projectId, checkId) {
  const path = `/api/site-projects/${encode(projectId)}/domains/availability-checks/${encode(checkId)}/events`;
  const base = trimTrailingSlash(apiUrl());
  if (!base) return path;
  return `${base}${path}`;
}
